import { useCallback, useEffect } from 'react';
import { chain } from 'underscore';

import { useMemberId } from '@trello/authentication';
import { isPaidManagedEnterpriseMember } from '@trello/business-logic/member';
import { freeBoardsRemaining } from '@trello/business-logic/organization';
import { Entitlements } from '@trello/entitlements';
import { isPossiblyValidOrgName } from '@trello/organizations';
import { useSharedState } from '@trello/shared-state';
import { workspaceState } from '@trello/workspace-state';

import { createMenuState, initialState } from './createMenuState';
import { usePreSelectWorkspaceQuery } from './PreSelectWorkspaceQuery.generated';

export const usePreSelectWorkspace = (preSelectedWorkspaceId?: string) => {
  const memberId = useMemberId();
  const [, setMenuState] = useSharedState(createMenuState);
  const [workspaceStateValue] = useSharedState(workspaceState);
  const { data } = usePreSelectWorkspaceQuery({
    variables: { memberId },
    waitOn: ['MemberHeader', 'MemberBoards'],
  });
  const isPaidManagedEntMember = isPaidManagedEnterpriseMember({
    confirmed: data?.member?.confirmed,
    idEnterprise: data?.member?.idEnterprise,
    enterpriseLicenses: data?.member?.enterpriseLicenses,
  });

  const isTeamValidForPreSelect = (teamId: string) => {
    const team = data?.member?.organizations.find((org) => org.id === teamId);
    if (team) {
      return (
        !isPaidManagedEntMember || Entitlements.isEnterprise(team.offering)
      );
    }
    return false;
  };

  const extractTeamFromPathname = () => {
    const pathname = window.location.pathname;
    const match = pathname.match(/\/([^/]+)\/?([^/]+)?/);
    if (!match) {
      return null;
    }
    const orgName = match[1];
    if (!isPossiblyValidOrgName(orgName)) {
      return null;
    }

    const team = data?.member?.organizations.find(
      (org) => org.name === orgName,
    );
    if (team) {
      if (!isPaidManagedEntMember || Entitlements.isEnterprise(team.offering)) {
        return team.id;
      }
    }
    return null;
  };

  const getTeamClosestToBoardLimit = () => {
    const teams = data?.member?.organizations;
    if (!teams) {
      return null;
    }
    const bestTeam = chain(teams)
      .filter((team) => freeBoardsRemaining(team) !== undefined)
      .sortBy((team) => freeBoardsRemaining(team))
      .first()
      .value();
    if (bestTeam) {
      if (
        !isPaidManagedEntMember ||
        Entitlements.isEnterprise(bestTeam.offering)
      ) {
        return bestTeam.id;
      }
    }
    return null;
  };

  const getSomeValidTeam = () => {
    const teams = data?.member?.organizations;
    if (!teams) {
      return null;
    }
    for (const team of teams) {
      if (!isPaidManagedEntMember || Entitlements.isEnterprise(team.offering)) {
        return team.id;
      }
    }
    return null;
  };

  const resetState = useCallback(() => {
    setMenuState({
      ...initialState,
      selectedTeamId: null,
    });
  }, [setMenuState]);

  useEffect(() => {
    // We choose the selected team ID based on the following priority:
    // 1. If an id is passed in as a prop
    // 2. If our workspace state is defined
    // 3. If a workspace is defined in the url
    // 4. The team closest to its board limit
    // 5. Any team
    if (
      preSelectedWorkspaceId &&
      isTeamValidForPreSelect(preSelectedWorkspaceId)
    ) {
      setMenuState({
        selectedTeamId: preSelectedWorkspaceId,
      });
    } else if (
      workspaceStateValue.workspaceId &&
      isTeamValidForPreSelect(workspaceStateValue.workspaceId)
    ) {
      setMenuState({
        selectedTeamId: workspaceStateValue.workspaceId,
      });
    } else {
      const bestEffortTeamId =
        extractTeamFromPathname() ||
        getTeamClosestToBoardLimit() ||
        getSomeValidTeam();
      if (bestEffortTeamId) {
        setMenuState({
          selectedTeamId: bestEffortTeamId,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, preSelectedWorkspaceId]);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);
};
